import React from 'react';
import Typography from '@material-ui/core/Typography';
import { Tooltip, createStyles, makeStyles } from '@material-ui/core';
import {
    Timeline,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    TimelineOppositeContent,
    TimelineSeparator,
} from '@material-ui/lab';

import ArrowForwardOutlinedIcon from '@material-ui/icons/ArrowForwardOutlined';
import ArrowUpwardOutlinedIcon from '@material-ui/icons/ArrowUpwardOutlined';
import AutorenewOutlinedIcon from '@material-ui/icons/AutorenewOutlined';
import ChatOutlinedIcon from '@material-ui/icons/ChatOutlined';
import CheckOutlinedIcon from '@material-ui/icons/CheckOutlined';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import HistoryOutlinedIcon from '@material-ui/icons/HistoryOutlined';
import LockOpenOutlinedIcon from '@material-ui/icons/LockOpenOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import CloseIcon from '@material-ui/icons/Close';
import PersonAddOutlinedIcon from '@material-ui/icons/PersonAddOutlined';
import RotateRightOutlinedIcon from '@material-ui/icons/RotateRightOutlined';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';

import { auditTrailStyles } from './AuditTrailTimeline.style';
import { noDataText } from '../../RecentActivity.style';

const ICON_STYLE = { fontSize: 14, color: '#fff' };

const useAuditTrailsStyles = makeStyles(() => createStyles(auditTrailStyles));

const getAuditIcon = ({ operation, formName, formType }) => {
    let icon;
    let bgColor;

    switch (true) {
        case operation === 'process':
        case operation === 'Created':
        case operation === 'taskCreated':
            bgColor = '#7A00FF';
            icon = <SettingsOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Accept':
        case operation === 'Complete':
        case operation === 'completedObligation':
            bgColor = '#27B47C';
            icon = <CheckOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Modify' && formName === 'workflow' && formType === 'Init':
            bgColor = '#3082AA';
            icon = <RotateRightOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Modify' && formName === 'workflow' && formType === 'ReInit':
            bgColor = '#0095FF';
            icon = <AutorenewOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Modify' && formName === 'workflow' && formType === 'Comment':
            bgColor = '#8E8E8E';
            icon = <ChatOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Modify' && formName === 'workflow' && formType === 'Execute':
            bgColor = '#27B47C';
            icon = <CheckOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Modify' && formName === 'workflow' && formType === 'Reject':
        case operation === 'rejectObligation':
            bgColor = '#F52F55';
            icon = <ClearOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Update':
            bgColor = '#ED6A30';
            icon = <EditOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Assign document':
            bgColor = '#FEAD2C';
            icon = <PersonAddOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Moved':
            bgColor = '#523FFF';
            icon = <ArrowForwardOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Upload':
            bgColor = '#FF3FEC';
            icon = <ArrowUpwardOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'filesplit':
            bgColor = '#BF00FF';
            icon = <FileCopyOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'UnLocked':
            bgColor = '#27B47C';
            icon = <LockOpenOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Restore':
            bgColor = '#C7317E';
            icon = <HistoryOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Locked':
            bgColor = '#526172';
            icon = <LockOutlinedIcon style={ICON_STYLE} />;
            break;
        case operation === 'Delete':
            bgColor = '#FE2C2C';
            icon = <CloseIcon style={ICON_STYLE} />;
            break;
        default:
            bgColor = '#B0BEC5';
            icon = <EditOutlinedIcon style={ICON_STYLE} />;
    }

    return { icon, bgColor };
};

const EventItem = ({ e, last }) => {
    const classes = useAuditTrailsStyles();
    const legend = getAuditIcon({
        operation: e?.operation,
        formName: e?.formName,
        formType: e?.formType,
    });

    return (
        <TimelineItem>
            <TimelineOppositeContent className={classes.opposite}>
                {e?.createdDate ? new Date(String(e.createdDate).trim()).toLocaleString() : ''}
            </TimelineOppositeContent>
            <TimelineSeparator style={{ marginTop: '0px' }}>
                <TimelineDot className={classes.dot} style={{ backgroundColor: legend?.bgColor }}>
                    {legend?.icon}
                </TimelineDot>
                {!last && <TimelineConnector className={classes.connector} />}
            </TimelineSeparator>
            <TimelineContent className={classes.content}>
                <Tooltip title={e?.message || ''}>
                    <Typography className={classes.title}>{e?.message || ''}</Typography>
                </Tooltip>
                <Typography className={classes.subtitle}>By {e?.userName || ''}</Typography>
            </TimelineContent>
        </TimelineItem>
    );
};

const AuditTrailTimeline = ({ auditTrails }) => {
    if (!auditTrails || auditTrails.length === 0) {
        return <div style={noDataText}>No audit history available yet.</div>;
    }

    return (
        <Timeline align="left" style={{ padding: '0px' }}>
            {auditTrails.map((res, index) => (
                <EventItem
                    key={`auditlog-${res._id}`}
                    e={res}
                    last={index === auditTrails.length - 1}
                />
            ))}
        </Timeline>
    );
};

export default AuditTrailTimeline;
